import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiError } from '@/lib/api';
import { toast } from '@/lib/toast';
import { invalidateMapInit } from '@/lib/map/mapInitCache';
import { transferTargetFarms, validateTransfer } from '../structureLogic';
import { useCreateFieldTransfer } from '../hooks/useStructureQueries';

export interface FarmOption {
  id: string;
  name: string;
  archived?: boolean;
}

interface TransferFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fieldId: string;
  fieldName: string;
  currentFarmId: string;
  currentFarmName?: string | null;
  areaHa?: number | null;
  /** Все хозяйства тенанта (активные). Архивные исключаются в логике. */
  farms: FarmOption[];
  /** Колбэк после успешной передачи — родитель перезагружает detail. */
  onTransferred?: () => void;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Передача поля в другое хозяйство (POST /fields/{id}/transfers): приёмник +
 * дата + примечание. Приёмник — только активные хозяйства, не текущее
 * (архивные исключены — краевой случай из wireframe). После успеха —
 * invalidateMapInit (поле сменило хозяйство → chip-агрегаты /map устарели).
 */
export function TransferFieldDialog({
  open,
  onOpenChange,
  fieldId,
  fieldName,
  currentFarmId,
  currentFarmName,
  areaHa,
  farms,
  onTransferred,
}: TransferFieldDialogProps) {
  const create = useCreateFieldTransfer(fieldId);
  const [toFarmId, setToFarmId] = useState<string | null>(null);
  const [date, setDate] = useState(today());
  const [note, setNote] = useState('');
  const [farmError, setFarmError] = useState<string | undefined>();
  const [dateError, setDateError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | undefined>();

  useEffect(() => {
    if (open) {
      setToFarmId(null);
      setDate(today());
      setNote('');
      setFarmError(undefined);
      setDateError(undefined);
      setSubmitError(undefined);
    }
  }, [open]);

  const targets = useMemo(
    () => transferTargetFarms(farms, currentFarmId),
    [farms, currentFarmId],
  );

  const submit = async () => {
    const v = validateTransfer({
      toFarmId,
      transferredOn: date,
      fromFarmId: currentFarmId,
    });
    setFarmError(v.farmError);
    setDateError(v.dateError);
    if (!v.ok || !toFarmId) return;

    try {
      await create.mutateAsync({
        to_farm_id: toFarmId,
        transferred_on: date,
        note: note.trim() || null,
      });
      invalidateMapInit();
      const toName = targets.find((f) => f.id === toFarmId)?.name ?? 'хозяйство';
      toast.success(`«${fieldName}» передано в «${toName}»`);
      onTransferred?.();
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) setSubmitError(e.detail);
      else setSubmitError('Не удалось передать поле');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Передать «{fieldName}» в другое хозяйство</DialogTitle>
          <DialogDescription>
            Поле сменит владельца. Прошлые операции и осмотры останутся привязаны
            к полю.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>
              В какое хозяйство <span className="text-destructive">*</span>
            </Label>
            <Select value={toFarmId ?? undefined} onValueChange={setToFarmId}>
              <SelectTrigger aria-invalid={!!farmError}>
                <SelectValue placeholder="Выберите хозяйство…" />
              </SelectTrigger>
              <SelectContent>
                {targets.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Из «{currentFarmName ?? '—'}». Архивные хозяйства недоступны.
            </p>
            {farmError && <p className="text-xs text-destructive">{farmError}</p>}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="transfer-date">
                Дата передачи <span className="text-destructive">*</span>
              </Label>
              <Input
                id="transfer-date"
                type="date"
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                  setDateError(undefined);
                }}
                aria-invalid={!!dateError}
              />
              {dateError && <p className="text-xs text-destructive">{dateError}</p>}
            </div>
            <div className="space-y-1.5">
              <Label>Га</Label>
              <div className="flex h-10 items-center rounded-md border border-input bg-secondary/30 px-3 text-sm text-muted-foreground">
                {areaHa != null ? `${areaHa.toFixed(1)} (всё поле)` : '— (всё поле)'}
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="transfer-note">
              Примечание{' '}
              <span className="font-normal text-muted-foreground">· необязательно</span>
            </Label>
            <textarea
              id="transfer-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="напр. перераспределение земель между хозяйствами кластера"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            Отмена
          </Button>
          <Button
            onClick={submit}
            disabled={create.isPending}
          >
            <ArrowLeftRight className="h-4 w-4" />
            {create.isPending ? 'Передаём…' : 'Передать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
